import React, { useEffect, useState } from "react";
import { useStripe, useElements, CardElement, AddressElement } from "@stripe/react-stripe-js";
import API_BASE_URL from "./config"; // Use API_BASE_URL from config
import "./Checkout.css"; // Ensure proper styling

const CheckoutForm = ({
  email,
  setEmail,
  verifyEmail,
  isVerified,
  verificationCode,
  setVerificationCode,
  handleVerifyCode,
  showVerificationInput,
  setShowVerificationInput,
  setEmailMessage,
  emailMessage,
  setCodeMessage,
  codeMessage,
  paymentSuccess,  // ✅ Payment success state
  setPaymentSuccess,
  companyLogo,
  loading,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [phone, setPhone] = useState("");
  const [accountName, setAccountName] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productError, setProductError] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [imageError, setImageError] = useState(false); // ✅ Track if error has occurred

  // ✅ Fetch Products from API
  useEffect(() => {
    fetch(`${API_BASE_URL}/products`)
      .then((res) => res.json())
      .then((data) => {
        if (data && Array.isArray(data) && data.length > 0) {
          setProducts(data);
          setSelectedProduct(data[0]); // ✅ Set default product only if data exists
        } else {
          setProductError("No products available.");
        }
      })
      .catch(() => setProductError("Failed to load products."));
  }, []);

  // ✅ Calculate total with tax (Assuming 10% tax rate)
  const [taxAmount, setTaxAmount] = useState(0);

  const fetchTax = async () => {
    if (selectedProduct && billingAddress) {
      try {
        const response = await fetch(`${API_BASE_URL}/get-product-tax`, {  // ✅ Replace API endpoint
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            productId: selectedProduct.id,
            email,
            accountName,
            billingAddress,
          }),
        });

        const data = await response.json();
        setTaxAmount(data.taxAmount || 0);
      } catch (error) {
        console.error("❌ Failed to fetch tax:", error);
        setTaxAmount(0);
      }
    }
  };

  // Fetch tax when billingAddress changes
  useEffect(() => {
    fetchTax();
  }, [billingAddress, selectedProduct]);

  const subtotal = selectedProduct ? selectedProduct.price : 0;
  const total = subtotal + taxAmount;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isVerified) return alert("Verify your email first.");

    setPaymentStatus("Processing subscription...");

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardElement),
      billing_details: {
        name: accountName,
        email: email,
        phone: phone,
        address: { line1: billingAddress },
      },
    });

    if (error) {
      setPaymentStatus("❌ Payment failed: " + error.message);
      return;
    }

    console.log("✅ Payment method created:", paymentMethod.id);

    // ✅ Step 1: Create Subscription
    const response = await fetch(`${API_BASE_URL}/create-subscription`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        accountName,
        priceId: selectedProduct?.id, // ✅ Ensure this is a valid Stripe Price ID
        paymentMethodId: paymentMethod.id, // ✅ Use PaymentMethod ID
      }),
    });

    const data = await response.json();

    if (!response.ok || !data.clientSecret) {
      setPaymentStatus("❌ Error: " + (data.error || "Subscription creation failed"));
      return;
    }

    console.log("✅ Subscription created:", data.subscriptionId);
    setPaymentStatus("✅ Subscription Successful!");
    setPaymentSuccess(true); // ✅ Show Thank You message
  };

  return (
    <div className="main-container flex flex-col md:flex-row items-top justify-center min-h-screen bg-neutral-50 p-6">
      {/* ✅ Left Column - Product Details */}
      <div className="max-w-md p-8 ml-auto">
        <img alt="BW Global Pte Ltd logo" src={companyLogo} className="HeaderImage-img" loading="lazy"></img>
        <div className="text-left mt-8">
          <p className="text-md text-gray-500 font-normal">Subscribe to Freelancer License</p>
          <div className="flex items-baseline justify-start space-x-1 mt-1 mb-8">
            <span className="text-4xl font-semibold text-gray-900">${subtotal.toFixed(2)}</span>
            <span className="text-sm font-medium text-gray-500">per month</span>
          </div>
        </div>
        {/* ✅ Product Details */}
        <div className="flex items-start space-x-4">
          <img
            src={imageError ? "" : selectedProduct?.image || "/fallback-product.png"}
            alt={selectedProduct?.name || "Product Image"}
            className="w-16 h-16 rounded-md shadow-md mt-2"
            onError={(e) => {
              if (!imageError) {
                setImageError(true); // ✅ Prevent infinite loop
                e.target.src = "/fallback-product.png";
              }
            }}
          />
          <div>
            <h3 className="text-md font-semibold text-gray-900">Freelancer License</h3>
            <p className="text-sm text-gray-600 mt-2">
              {selectedProduct?.description || "For independent designers and product developers. Purchase and start using today."}
            </p>
          </div>
          <p className="text-md font-medium text-gray-900">${subtotal.toFixed(2)}</p>
        </div>

        {/* ✅ Divider */}
        <hr className="my-4 border-gray-300" />

        {/* ✅ Pricing Breakdown */}
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-700">Subtotal</span>
            <span className="text-gray-900 font-medium">${subtotal.toFixed(2)}</span>
          </div>

          {/* ✅ Promo Code Input */}
          <div className="flex items-center space-x-2">
            <input
              type="text"
              name="promoCode"
              placeholder="Enter Promo Code"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 text-sm"
            />
            <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200">
              Apply
            </button>
          </div>

          {/* ✅ Tax Calculation */}
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-1">
              <span className="text-gray-700">Tax</span>
              <span className="text-gray-400 text-sm">(Enter address to calculate)</span>
            </div>
            <span className="text-gray-900 font-medium">${taxAmount ? taxAmount.toFixed(2) : "—"}</span>
          </div>
        </div>

        {/* ✅ Divider */}
        <hr className="my-4 border-gray-300" />

        {/* ✅ Total Amount */}
        <div className="flex justify-between text-md font-semibold">
          <span className="text-gray-900">Total due today</span>
          <span className="text-gray-900">${total.toFixed(2)}</span>
        </div>
      </div>
      {/*
      <div className="product-info w-full md:w-1/2 flex justify-end">
        <div className="max-w-sm p-8 ml-auto">
          {selectedProduct ? (
            <>
              <img alt="BW Global Pte Ltd logo" src="https://d1wqzb5bdbcre6.cloudfront.net/4fc55bc783fcbaf5657b66357c68d8eba523056c092157a36b09deb3309e8ff4/68747470733a2f2f66696c65732e7374726970652e636f6d2f66696c65732f4d44423859574e6a6446387853316c4c636b4e44646b704b4e47707a59336c6a66475a6662476c325a56396f4f444245516a5a724e6b4a4564566c43556d35684f5870525957563556446730306e4d6263356e496f" className="HeaderImage-img" loading="lazy"></img>
              <p className="text-md text-gray-600">Subscribe to Freelancer License</p>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">${subtotal.toFixed(2)} 
              <span className="text-sm font-medium text-gray-600">/per month</span></h2>
              <img
                src={selectedProduct?.image || "/fallback-product.png"}
                alt={selectedProduct?.name || "Product Image"}
                className="product-logo w-full max-w-20 mx-auto rounded-sm shadow-md"
                onError={(e) => e.target.src = "/fallback-product.png"} // ✅ Use a safe fallback
              />
              <p className="text-sm text-gray-600">{selectedProduct.description}</p>
              <div className="pricing">
                <p>Subtotal: ${subtotal.toFixed(2)}</p>
                <p>Tax: ${taxAmount.toFixed(2)}</p>
                <h3>Total: ${total.toFixed(2)}</h3>
              </div>
              <label>Promotion Code:</label>
              <input
                type="text"
                name="promoCode"
                placeholder="Enter Promo Code"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
              />
            </>
          ) : (
            <p style={{ color: "red" }}>{productError}</p>
          )}
        </div>
      </div>*}
      {/* ✅ Right Column - Checkout Form */}
      <div className="checkout-form w-full md:w-1/2">
        <div className="max-w-md p-8">
          {/* ✅ Show Thank You Message If Payment Is Successful */}
          {paymentSuccess ? (
            <div className="text-center">
              <h2 className="text-2xl font-bold text-green-600">🎉 Thank You!</h2>
              <p className="text-gray-700 mt-4">Your payment was successful.</p>
            </div>
          ) : (
            <>
              {/* ✅ Email Input */}
              <div className="mb-5">
                <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-500 dark:text-white">Email</label>
                <input
                  type="email"
                  name="email"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setVerificationCode("");
                    setShowVerificationInput(false);
                    setEmailMessage("");
                  }}
                  onBlur={verifyEmail}
                  autoComplete="email"
                  required
                />
                {loading && <p className="text-sm text-gray-600 mt-2">⏳ Checking email...</p>}
                <p className="text-sm text-gray-600 mt-2">{emailMessage}</p>
              </div>

              {/* ✅ OTP Input */}
              {showVerificationInput && (
                <div className="mb-5">
                  <input
                    type="text"
                    name="verificationCode"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Enter verification code"
                    value={verificationCode}
                    onChange={(e) => {
                      setVerificationCode(e.target.value);
                      setCodeMessage("");
                    }}
                  />
                  <button
                    className="w-full font-medium py-3 rounded-lg mt-4 bg-lime-500 hover:bg-lime-600 text-white"
                    onClick={handleVerifyCode}
                    disabled={!verificationCode}
                  >
                    Verify
                  </button>
                  <p className="text-sm text-gray-600 mt-2">{codeMessage}</p>
                </div>
              )}

              {/* ✅ Phone Input */}
              <div className="mb-5">
                <label htmlFor="phone" className="block mb-2 text-sm font-small text-gray-500 dark:text-white">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Phone (800) 555-0175"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
              </div>

              {/* ✅ Account Name Input */}
              <div className="mb-5">
                <label htmlFor="accountName" className="block mb-2 text-sm font-small text-gray-500 dark:text-white">Account Name</label>
                <input
                  type="text"
                  name="accountName"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Full Name"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  required
                />
              </div>

              {/* ✅ Billing Address Input */}
              <div className="mb-5">
                <label htmlFor="billingAddress" className="block mb-2 text-sm font-small text-gray-500 dark:text-white">Billing Address</label>
                {/* <AddressElement
    options={{ mode: "billing" }} // ✅ Autofill for billing address
  />*/}

                <input
                  type="text"
                  name="billingAddress"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Billing Address"
                  value={billingAddress}
                  onChange={(e) => setBillingAddress(e.target.value)}
                  required
                />
              </div>

              {/* ✅ Card Details */}
              <label htmlFor="CardElement" className="block mb-2 text-sm font-small text-gray-500 dark:text-white">Card details</label>
              <div className="p-3 border border-gray-300 rounded-md">
                <CardElement />
              </div>
              {/* ✅ Terms of Service & Privacy Policy Checkbox */}
              <div className="mb-5 flex items-top mt-4">
                <input
                  type="checkbox"
                  id="terms"
                  checked={isTermsAccepted}
                  onChange={() => setIsTermsAccepted(!isTermsAccepted)}
                  className="w-5 h-5 mr-2 cursor-pointer "
                />
                <label htmlFor="terms" className="text-sm text-gray-500">
                  I agree to BW Global Pte Ltd's
                  <a href="/terms" className="text-blue-600 underline"> Terms of Service </a>
                  and
                  <a href="/privacy" className="text-blue-600 underline"> Privacy Policy</a>.
                </label>
              </div>
              {/* ✅ Payment Button */}
              <button
                onClick={handleSubmit}
                disabled={!isVerified || !selectedProduct || !billingAddress || !isTermsAccepted || paymentStatus === "Processing payment..."}
                className={`w-full font-medium py-3 rounded-lg mt-4 
    ${!isVerified || !selectedProduct || !billingAddress || !isTermsAccepted || paymentStatus === "Processing payment..."
                    ? "bg-gray-400 cursor-not-allowed opacity-50"
                    : "bg-lime-600 hover:bg-lime-700 text-white"}`}
              >
                {paymentStatus === "Processing payment..." ? "Processing..." : "Subscribe"}
              </button>
              <p className="text-sm text-gray-600 mt-2">{paymentStatus}</p>
              <p className="text-sm text-gray-400 mt-2 text-center
">By confirming your subscription, you allow BW Global Pte Ltd to charge you for future payments in accordance with their terms. You can always cancel your subscription.</p>
            </>
          )}
        </div>
      </div>

    </div>
  );
};

export default CheckoutForm;